import React from 'react';
import { MessageCircle, Calendar, CheckCircle, Shield, Clock, AlertTriangle, Home, Building2, Archive } from 'lucide-react';
import ServicePageLayout from '../components/ServicePageLayout';
import { P } from '../constants';

const data = {
  seo: {
    title: 'Messie Wohnung Wien räumen » Professionell & diskret – Wohnungsräumung Wien',
    description: 'Messie Wohnung Wien räumen ✓ Professionell & diskret ✓ Keine Fragen, keine Vorwürfe ✓ Fixpreisgarantie ✓ Kostenlose Besichtigung. Jetzt anfragen!',
    keywords: 'Messie Wohnung Wien, Messie Wohnung räumen Wien, Messie Räumung Wien, Messie Entrümpelung Wien',
    canonical: '/messie-wohnung-wien',
    h1short: 'Messie Wohnung Wien',
  },
  schemas: [{
    '@context': 'https://schema.org',
    '@type': 'Service',
    'name': 'Messie Wohnung räumen Wien',
    'provider': { '@type': 'LocalBusiness', 'name': 'Wohnungsräumung Wien' },
    'areaServed': 'Wien',
    'description': 'Professionelle Räumung und Reinigung von Messie-Wohnungen in Wien. Diskret, gründlich und zum Fixpreis.',
    'offers': { '@type': 'Offer', 'price': '890', 'priceCurrency': 'EUR' },
  }],
  hero: {
    badge: 'Diskret & ohne Vorwürfe',
    headline: <><span>Messie Wohnung<br />Wien räumen</span><br /><span className="text-[#E8722A]">professionell & diskret.</span></>,
    subline: 'Kein Urteil. Kein Aufhebens. Nur Ergebnis.',
    bullets: [
      'Kein Aufsehen – unauffällige Fahrzeuge, diskrete Abwicklung',
      'Keine Fragen, keine Vorwürfe – wir räumen einfach',
      'Verbindlicher Fixpreis nach kostenloser Besichtigung',
      'Fachgerechte Entsorgung auch bei hygienisch belastetem Material',
      'Besenrein übergeben – bereit zur Neuvermietung',
    ],
    imageSrc: P.clutteredBoxes,
    imageAlt: 'Messie Wohnung Wien – vor der Räumung',
  },
  formTitle: 'Messie-Räumung anfragen –',
  serviceName: 'Messie-Wohnung',
  serviceCard: {
    heading: 'Messie Wohnung räumen Wien – kein Urteil, nur Ergebnis',
    paragraphs: [
      'Keine Erklärungen nötig. Keine unangenehmen Fragen. Keine Urteile.',
      'Wir kommen mit einem unauffälligen Fahrzeug, räumen alles raus und übergeben besenrein. So diskret wie möglich. So effizient wie nötig.',
    ],
    listItems: [
      'Kein Aufsehen – unauffällige Fahrzeuge, keine Nachbarn informiert',
      'Erfahrung mit extremen Verschmutzungen und schwierigen Situationen',
      'Fachgerechte Entsorgung – auch bei hygienisch belastetem Material',
      'Keine Vorbereitung nötig – wir nehmen alles so wie es ist',
      'Besenreine Übergabe – bereit zur Neuvermietung',
    ],
    imageSrc: P.worker1,
    imageAlt: 'Messie Wohnung räumen Wien – professionelles Team',
  },
  benefits: [
    { icon: <AlertTriangle size={22} />, title: 'Spezialisiert', desc: 'Erfahrung mit extremen Verschmutzungen, biologischen Belastungen und schwierigen Zugängen.' },
    { icon: <Shield size={22} />, title: 'Diskret', desc: 'Absolute Verschwiegenheit. Kein Auffallen bei Nachbarn oder Dritten.' },
    { icon: <Clock size={22} />, title: 'Schnell', desc: 'Kurzfristige Terminvergabe – auch bei dringendem Handlungsbedarf.' },
    { icon: <CheckCircle size={22} />, title: 'Kein Aufwand', desc: 'Keine Vorbereitung, keine Sortierung, keine Erklärungen. Wir nehmen alles so wie es ist.' },
  ],
  process: [
    { icon: <MessageCircle size={24} />, title: 'Unverbindliche Anfrage', desc: 'Beschreiben Sie die Situation kurz – telefonisch, per WhatsApp oder im Formular. Wir stellen keine unangenehmen Fragen.' },
    { icon: <Calendar size={24} />, title: 'Diskreter Besichtigungstermin', desc: 'Wir kommen in einem unauffälligen Fahrzeug, besichtigen die Wohnung und erstellen einen verbindlichen Fixpreis.' },
    { icon: <CheckCircle size={24} />, title: 'Räumung & Übergabe', desc: 'Wir räumen aus, entsorgen fachgerecht und übergeben besenrein. Das ist unser Standardversprechen.' },
  ],
  pricing: [
    { label: 'Kleine Messie-Wohnung (1–2 Zi.)', price: 'ab €890', icon: <Home size={20} />, highlight: false },
    { label: 'Mittlere Messie-Wohnung (3 Zi.)', price: 'ab €1.490', icon: <Building2 size={20} />, highlight: true },
    { label: 'Stark belastete Wohnung / Haus', price: 'auf Anfrage', icon: <Archive size={20} />, highlight: false },
  ],
  faq: [
    { q: 'Was ist eine Messie-Wohnung – und warum ist die Räumung teurer?', a: 'Stark vernachlässigte Wohnungen mit massenhaft Gegenständen, oft mit Schmutz oder hygienischen Belastungen. Der Aufwand ist schlicht größer – daher die höheren Kosten. Alles transparent nach Besichtigung.' },
    { q: 'Wirklich diskret?', a: 'Ja. Unauffällige Fahrzeuge, keine Schilder, keine Informationen an Nachbarn oder Hausverwaltung. Was in der Wohnung passiert, bleibt zwischen uns.' },
    { q: 'Muss ich die Wohnung vorher aufräumen oder sortieren?', a: 'Nein. Nichts vorbereiten, nichts sortieren, nichts erklären. Wir räumen alles so wie es ist. Das ist genau unser Job.' },
    { q: 'Was kostet die Räumung einer Messie-Wohnung?', a: 'Kleine Wohnungen ab €890, mittelgroße ab €1.490. Stark belastete Objekte auf Anfrage. Verbindlicher Fixpreis nach der kostenlosen Besichtigung.' },
    { q: 'Wie geht Ihr Team mit hygienisch belastetem Material um?', a: 'Geschult, ausgerüstet, erfahren. Schutzausrüstung, fachgerechte Entsorgung nach österreichischen Vorschriften. Das ist unser Alltag – kein Problem.' },
  ],
  relatedServices: [
    { title: 'Wohnungsräumung', slug: '/wohnungsraeumung-wien', icon: <Home size={18} /> },
    { title: 'Haushaltsauflösung', slug: '/haushaltsaufloesung-wien', icon: <Archive size={18} /> },
    { title: 'Nachlassräumung', slug: '/nachlassraeumung-wien', icon: <CheckCircle size={18} /> },
    { title: 'Kellerräumung', slug: '/kellerraeumung-wien', icon: <Building2 size={18} /> },
  ],
};

export default function MessieWohnungPage() {
  return <ServicePageLayout data={data} />;
}
